import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { RevuesPressService } from '../revues-presse.service';

export interface Revue {
  id_press_article: number;
  title_ln1: string;
  source_ln1?: string;
  publish_date: string;
  article_url?: string;
  snippet?: string;
  status?: 'ACTI' | 'INAC' | 'ARCH';
  status_label?: string;
  press_tags?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

@Component({
  selector: 'app-revues-list',
  standalone: false,
  templateUrl: './revues-list.component.html',
  styleUrls: ['./revues-list.component.scss']
})
export class RevuesListComponent implements OnInit, OnDestroy {
  revues = signal<Revue[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);
  currentPage = signal(1);
  searchQuery = signal('');
  selectedStatus = signal('');
  totalRevues = signal(0);
  perPage = signal(8);
  pagination = signal<Pagination>({ page: 1, limit: 8, total: 0, totalPages: 0 });

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private revuesPressService: RevuesPressService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadRevues(1, true);
    });
    this.loadRevues(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  private loadRevues(page: number, filtering = false): void {
    if (filtering) {
      this.isFiltering.set(true);
    } else {
      this.isLoading.set(true);
    }
    this.errorMessage.set(null);

    this.revuesPressService.getRevues(page, this.pagination().limit, this.searchQuery(), this.selectedStatus())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.revues.set(response.data || []);
          const total = response.pagination?.total || response.total || 0;
          this.pagination.set({
            page,
            limit: this.pagination().limit,
            total,
            totalPages: Math.ceil(total / this.pagination().limit),
          });
          this.isLoading.set(false);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement des revues de presse');
          this.isLoading.set(false);
          this.isFiltering.set(false);
        }
      });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadRevues(1, true);
  }

  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.currentPage.set(page);
    this.loadRevues(page);
  }

  onAddNew(): void {
    this.router.navigate(['/federation/revues-presse/new']);
  }

  onView(id: number): void {
    this.router.navigate(['/federation/revues-presse', id]);
  }

  onEdit(id: number | undefined): void {
    if (!id) return;
    this.router.navigate(['/federation/revues-presse', id, 'edit']);
  }

  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = { ACTI: 'En ligne', INAC: 'Hors ligne', ARCH: 'Archivé' };
    return map[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    if (status === 'ARCH') return 'bg-stone-100 text-stone-500 border-stone-200';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

}
